import { createStyles, withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { WithStyles } from '@material-ui/styles';
import * as React from 'react';
import i18next from '../resources/i18n';

interface IState {
	pageNo?: number;
	pageSize: number;
	prevPageNo: number;
}

const styles = (_: Theme) =>
	createStyles({
		pageInput: {
			width: 50,
		},
		paginationButton: {
			width: '100%',
		},
		paginationColumn: {
			textAlign: 'center',
		},
	});

class CardPagenation extends React.Component<IProps, IState> {
	public constructor(props: IProps) {
		super(props);

		this.state = {
			pageNo: 1,
			pageSize: 5,
			prevPageNo: 1,
		};

		this.change = this.change.bind(this);
		this.blur = this.blur.bind(this);
		this.keyPress = this.keyPress.bind(this);
		this.applyPage = this.applyPage.bind(this);
		this.prev = this.prev.bind(this);
		this.next = this.next.bind(this);
	}

	public componentDidUpdate(prevProps: IProps) {
		if (this.props.dataSize !== prevProps.dataSize) {
			this.setState({ pageNo: 1, prevPageNo: 1 });
			this.changeDisplayData();
		}
	}

	public render() {
		const { classes } = this.props;
		const { pageNo } = this.state;
		const canPrev = this.getCanPrev(pageNo);
		const canNext = this.getCanNext(pageNo);

		return (
			<Paper className={this.props.className}>
				<Grid container={true}>
					<Grid
						item={true}
						xs={4}
						className={classes.paginationColumn}
					>
						<Button
							color="primary"
							onClick={this.prev}
							disabled={!canPrev}
							className={classes.paginationButton}
						>
							{i18next.t('CardPagenation_0001')}
						</Button>
					</Grid>
					<Grid
						item={true}
						xs={4}
						className={classes.paginationColumn}
					>
						<Input
							type="number"
							name="pageNo"
							value={this.state.pageNo}
							onChange={this.change}
							onBlur={this.blur}
							onKeyPress={this.keyPress}
							className={classes.pageInput}
						/>
						/{this.getMaxPage()}
					</Grid>
					<Grid
						item={true}
						xs={4}
						className={classes.paginationColumn}
					>
						<Button
							color="primary"
							onClick={this.next}
							disabled={!canNext}
							className={classes.paginationButton}
						>
							{i18next.t('CardPagenation_0002')}
						</Button>
					</Grid>
				</Grid>
			</Paper>
		);
	}

	/**
	 * ページ変更
	 * @param newPageNo 表示ページ数
	 */
	protected changePage(prevPageNo: number, newPageNo: number) {
		const minPage = 1;
		const maxPage = this.getMaxPage();
		if (newPageNo < minPage) {
			newPageNo = minPage;
		} else if (maxPage < newPageNo) {
			newPageNo = maxPage;
		}

		this.setState({ pageNo: newPageNo, prevPageNo }, () => {
			// 表示データ変更
			this.changeDisplayData();
		});
	}

	/**
	 * ページ番号入力処理(表示変更のみ)
	 * @param e イベントオブジェクト
	 */
	protected change(e: React.ChangeEvent<HTMLInputElement>) {
		const val = e.target.value;
		const { prevPageNo } = this.state;
		if (val === '') {
			this.setState({ pageNo: undefined });
			return;
		}

		const newPageNo: number = +val;
		if (Number.isNaN(newPageNo)) {
			this.setState({ pageNo: prevPageNo, prevPageNo });
		} else {
			this.setState({ pageNo: newPageNo, prevPageNo });
		}
	}

	/**
	 * ページ番号フォーカスアウト処理
	 * @param e イベントオブジェクト
	 */
	protected blur(e: React.FocusEvent<HTMLDivElement>) {
		e.preventDefault();
		this.applyPage();
	}

	/**
	 * キー押下処理
	 * @param e イベントオブジェクト
	 */
	protected keyPress(e: React.KeyboardEvent<HTMLDivElement>) {
		if (e.which === 13 || e.keyCode === 13) {
			this.applyPage();
		}
	}

	/**
	 * ページ設定処理
	 * @param e イベントオブジェクト
	 */
	protected applyPage() {
		const { pageNo, prevPageNo } = this.state;
		if (!pageNo) {
			this.changePage(prevPageNo, prevPageNo);
		} else {
			this.changePage(prevPageNo, pageNo);
		}
	}

	/**
	 * 前へボタン押下処理
	 */
	protected prev(e: React.MouseEvent<HTMLElement>) {
		const prevPageNo = this.state.pageNo || 1;
		const newPageNo = prevPageNo - 1;
		this.changePage(prevPageNo, newPageNo);
	}

	/**
	 * 次へボタン押下処理
	 */
	protected next(e: React.MouseEvent<HTMLElement>) {
		const prevPageNo = this.state.pageNo || 1;
		const newPageNo = prevPageNo + 1;
		this.changePage(prevPageNo, newPageNo);
	}

	/**
	 * 表示データ変更処理
	 */
	protected changeDisplayData() {
		if (this.props.onPageChange) {
			const { pageNo = 1, pageSize } = this.state;
			const { dataSize } = this.props;
			const maxPage = this.getMaxPage();
			const startRowIndex = (pageNo - 1) * pageSize;
			const endRowIndex =
				pageNo !== maxPage ? pageNo * pageSize - 1 : dataSize - 1;
			this.props.onPageChange(startRowIndex, endRowIndex);
		}
	}

	/**
	 * 前へボタン押下可否チェック
	 * @param pageNo ページ番号
	 */
	protected getCanPrev(pageNo?: number) {
		const dataSize = this.props.dataSize;
		const { pageSize, prevPageNo } = this.state;
		if (pageNo === undefined || Number.isNaN(pageNo)) {
			pageNo = prevPageNo;
		}
		return dataSize > pageSize && pageNo > 1;
	}

	/**
	 * 次へボタン押下可否チェック
	 * @param pageNo ページ番号
	 */
	protected getCanNext(pageNo?: number) {
		const maxPage = this.getMaxPage();
		const { prevPageNo } = this.state;
		if (pageNo === undefined || Number.isNaN(pageNo)) {
			pageNo = prevPageNo;
		}
		return pageNo < maxPage;
	}

	/**
	 * 最大ページ数取得
	 */
	protected getMaxPage() {
		const dataSize = this.props.dataSize;
		const { pageSize } = this.state;
		const maxPage = Math.ceil(dataSize / pageSize);
		return maxPage;
	}
}

export interface ICardPagenationProps {
	dataSize: number;
	onPageChange: (startRowIndex: number, endRowIndex: number) => void;
	className?: any;
}

const mergeProps = (ownProps: IOwnProps) => ({
	...ownProps,
});

type IOwnProps = WithStyles<typeof styles> & ICardPagenationProps;
type IProps = ReturnType<typeof mergeProps>;

const Styled = withStyles(styles)(CardPagenation);

export default Styled;



// WEBPACK FOOTER //
// ./src/components/CardPagenation.tsx